import { isNonProd } from "../environment";
import { sentryService } from "./service";

const reported = new Set<string>();

/**
 * Report a union member we have no branch for, without throwing.
 *
 * In production: reports to Sentry and continues. A binary older than the API
 * will legitimately meet types it predates; throwing would turn routine
 * version skew into a blank error screen.
 *
 * In non-prod (local + deployed dev): throws immediately. Reaching here in dev
 * almost always means generated types are stale, and a crash reads as "run
 * codegen" while a Sentry report in dev gets missed.
 *
 * Taking `never` keeps the compile-time check — a missed case still fails
 * typecheck.
 */
export function reportUnhandledVariant(value: never, context: string): void {
  report(value, context);
}

/**
 * Same reporting and dedupe, for positions where the compiler can't prove the
 * value is `never`.
 */
export function reportUnknownVariant(value: unknown, context: string): void {
  report(value, context);
}

function report(value: unknown, context: string): void {
  const typename =
    typeof value === "object" && value !== null && "__typename" in value
      ? String(value.__typename)
      : "unknown";

  // Fail fast outside production: reaching here in dev almost always means
  // generated types are stale, and a Sentry report in dev gets missed.
  if (isNonProd()) {
    throw new Error(`Unhandled variant in ${context}: ${typename}`);
  }

  // These sit in render paths — report each variant once per session.
  const key = `${context}:${typename}`;
  if (reported.has(key)) {
    return;
  }
  reported.add(key);

  sentryService.captureException(
    new Error(`Unhandled variant in ${context}: ${typename}`),
    { extra: { context, typename } },
  );
}
